import { Alert, Descriptions, Typography } from 'antd'
import { CapturedAssessmentDetails } from 'features/libra/inspectionPlanning/CapturedAssessmentDetails'
import { NiiAssessmentBasisCard } from 'features/libra/inspectionPlanning/NiiAssessmentBasisCard'
import { NiiAssessmentSelector } from 'features/libra/inspectionPlanning/NiiAssessmentSelector'
import { planningPreparationSchema } from 'features/libra/inspectionPlanning/planningPreparationConfig'
import { PlanningPreparationForm } from 'features/libra/inspectionPlanning/PlanningPreparationForm'
import { usePlanningBasis } from 'features/libra/inspectionPlanning/usePlanningBasis'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { useMemo } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const useValidateConfig: UseValidateConfigHook = ({ config }) =>
  useMemo(() => planningPreparationSchema.safeParse(config).success, [config])

export const Component = (
  props: LibraWorflowStepComponentProps<Record<string, unknown>>
) => {
  const { basis, ticketId, organizationId, readonly } = usePlanningBasis(
    props.config,
    props.readonly
  )
  if (basis.status === 'invalid')
    return (
      <Alert
        type='warning'
        showIcon
        title='NII assessment basis needs attention'
        description='The captured assessment or workflow settings could not be read. Preparation cannot continue until they are corrected; saved values have been preserved.'
      />
    )
  if (basis.status === 'uncaptured') {
    return (
      <NiiAssessmentSelector
        key={ticketId}
        config={basis.captureConfig}
        readonly={readonly}
      />
    )
  }
  const { config, summary, recommendations } = basis

  return (
    <Container>
      <NiiAssessmentBasisCard
        source={basis.source}
        zones={basis.zones}
        coverageByZone={basis.coverageByZone}
        organizationId={organizationId}
      />
      {basis.sourceDetails.length > 0 && (
        <Descriptions
          column={1}
          bordered
          items={basis.sourceDetails.map(({ value, ...detail }) => ({
            ...detail,
            children: value,
          }))}
        />
      )}
      {config.sourceContext && (
        <CapturedAssessmentDetails
          title={config.sourceContext.title}
          sections={basis.capturedSections}
        />
      )}
      {summary && <Typography.Paragraph>{summary}</Typography.Paragraph>}
      {recommendations.map((recommendation, index) => (
        <Typography.Paragraph key={index}>
          {recommendation}
        </Typography.Paragraph>
      ))}
      <PlanningPreparationForm config={config} readonly={readonly} />
    </Container>
  )
}
